import React, { ButtonHTMLAttributes } from 'react'
import { useNavigate } from 'react-router-dom'
import { Icon, IconName } from '~components/Icon'
import { Container, Inner, IconSide, IconMiddle, SuccessText } from './Button.styles'
import { ButtonProps, viewType, ViewType } from './Button.types'

const Button: React.FC<ButtonProps> = React.forwardRef<HTMLButtonElement, ButtonProps>((props, ref) => {
  const {
    color = 'primary',
    variant = 'solid',
    size = 'medium',
    shape = 'default',
    iconPosition = 'left',
    coloredOnSuccess = false,
    icon,
    onClick,
    isSuccess,
    disabled,
    customLoader,
    ...otherProps
  } = props

  const navigate = useNavigate()

  const defineComponent = () => {
    const renderAsCustom = props.as

    if (renderAsCustom) {
      return renderAsCustom
    }

    const renderAsLink = props.href || props.to

    if (renderAsLink) {
      return 'a'
    }

    return undefined
  }

  const defineType = (): ButtonHTMLAttributes<HTMLButtonElement>['type'] => {
    if ((!props.to && !props.href && !props.as) || props.as === 'button') return 'button'
    return undefined
  }

  const defineView = (): ViewType => {
    const viewColor = coloredOnSuccess && isSuccess ? 'success' : color
    const view = `${viewColor}-${variant}` as ViewType

    if (viewType.includes(view)) {
      return view
    }
    return viewType.find(type => type.includes(viewColor)) || `primary-${variant}`
  }

  const successElement =
    typeof isSuccess === 'string' ? (
      <SuccessText size={size}>{isSuccess}</SuccessText>
    ) : (
      <IconMiddle isAnimated={true}>
        <Icon name={IconName.check} size="large" />
      </IconMiddle>
    )

  const handleClick = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    if (disabled || props.isLoading || props.isSuccess) {
      e.preventDefault()
      return
    }

    if (props.to && !props.as) {
      e.preventDefault()
      navigate(props.to)
    }
    onClick?.(e)
  }

  return (
    <Container
      ref={ref}
      as={defineComponent()}
      href={props.to}
      type={defineType()}
      view={defineView()}
      size={size}
      shape={shape}
      disabled={disabled || props.isLoading || props.isSuccess}
      isDisabled={disabled}
      isSuccess={!!isSuccess}
      onClick={handleClick}
      {...otherProps}>
      <Inner isHidden={!!props.isLoading || !!isSuccess} shape={shape}>
        {icon && iconPosition === 'left' && (
          <IconSide size={size} position={iconPosition}>
            <Icon name={icon} />
          </IconSide>
        )}
        {props.children}
        {icon && iconPosition === 'right' && (
          <IconSide size={size} position={iconPosition}>
            <Icon name={icon} />
          </IconSide>
        )}
      </Inner>

      {isSuccess && !props.isLoading && successElement}
      {props.isLoading && <IconMiddle>{customLoader || <Icon name={IconName.spinner} />}</IconMiddle>}
    </Container>
  )
})

Button.displayName = 'Button'
export default Button
